import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui';
import {
  GetFunctionsByEventId, GetGuestsByEventId, GetGuestsByEventIdForMember,
  AddOrMoveFamilyMember, UpdateGuest, DeleteGuest,
} from '../../api/api';
import { fullName, fmtDate } from '../../utils/helpers';

const rowFunctionIds = (row) => {
  if (row.FunctionIds) return String(row.FunctionIds).split(',').map(v => v.trim()).filter(Boolean);
  return row.FunctionId ? [String(row.FunctionId)] : [];
};

// The families of this event: one entry per family HEAD, its members under it.
// The Guests screen is per invitation (person x function); this one is per
// household - it is where a family grows after the head was saved alone, and
// it is the list the invitation-card labels follow (one card per family).
export function useFamilies(event) {
  const { user, isAdmin, can } = useAuth();
  const toast = useToast();
  const me = user.UserId;

  const canView = isAdmin || can('CanMembers');
  const canEdit = isAdmin || can('CanMembersEdit') || can('CanMembers');
  const canDelete = isAdmin || can('CanMembersDelete') || can('CanMembers');

  const [rows, setRows] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [openFamily, setOpenFamily] = useState(null);   // GuestId of the head whose card is expanded
  const [addingTo, setAddingTo] = useState(null);       // the head a member is being added to

  const refresh = useCallback(() => {
    setLoading(true);
    const loader = isAdmin
      ? GetGuestsByEventId({ EventId: event.EventId })
      : GetGuestsByEventIdForMember({ EventId: event.EventId, UserId: me });
    loader.then(d => setRows(Array.isArray(d) ? d : []))
      .catch(() => toast('Failed to load guests'))
      .finally(() => setLoading(false));
  }, [event, isAdmin, me, toast]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    GetFunctionsByEventId({ EventId: event.EventId })
      .then(d => setFunctions(Array.isArray(d) ? d : []))
      .catch(() => setFunctions([]));
  }, [event]);

  const fnName = useCallback(
    (id) => functions.find(f => String(f.FunctionId) === String(id))?.FunctionName || '',
    [functions]
  );

  // One entry per family: the head (GuestId === FamilyId) plus its members.
  // Rows of the same person for several functions collapse into one member.
  const families = useMemo(() => {
    const byFamily = new Map();
    rows.forEach(r => {
      const famId = Number(r.FamilyId || 0);
      if (!famId) return;                                   // individuals are not families
      if (!byFamily.has(famId)) byFamily.set(famId, { familyId: famId, head: null, members: new Map(), functionIds: new Set() });
      const fam = byFamily.get(famId);
      const key = String(r.GuestId);
      if (!fam.members.has(key)) fam.members.set(key, r);
      if (String(r.GuestId) === String(famId)) fam.head = r;
      rowFunctionIds(r).forEach(id => fam.functionIds.add(String(id)));
    });
    const list = [...byFamily.values()].map(fam => {
      const members = [...fam.members.values()];
      const head = fam.head || members[0];
      return {
        familyId: fam.familyId,
        head,
        headName: fullName(head) || head?.GuestName || '',
        members: members.filter(m => String(m.GuestId) !== String(head?.GuestId)),
        size: members.length,
        functionNames: [...fam.functionIds].map(fnName).filter(Boolean),
        mine: isAdmin || String(head?.CreatedBy) === String(me),
      };
    });
    const needle = q.trim().toLowerCase();
    const matching = needle
      ? list.filter(f => (f.headName + ' ' + f.members.map(m => fullName(m)).join(' ') + ' ' + (f.head?.MobileNumber || ''))
        .toLowerCase().includes(needle))
      : list;
    return matching.sort((a, b) => a.headName.localeCompare(b.headName));
  }, [rows, q, fnName, isAdmin, me]);

  const totals = useMemo(() => ({
    families: families.length,
    people: families.reduce((s, f) => s + f.size, 0),
  }), [families]);

  // Adding a member re-uses the guest endpoint that enforces one family per
  // guest: a matching phone MOVES that guest in instead of duplicating them.
  const addMember = async (head, payload) => {
    await AddOrMoveFamilyMember({
      EventId: event.EventId,
      FamilyId: head.FamilyId || head.GuestId,
      HeadGuestId: head.GuestId,
      ...payload,
      CreatedBy: me,
      UpdatedBy: me,
    });
    toast('Added to the family');
    refresh();
  };

  const removeMember = async (member) => {
    if (!window.confirm(`Remove ${fullName(member)} from this family? The guest is deleted.`)) return;
    try {
      await DeleteGuest({ data: { Valid: 0, UpdatedBy: me }, where: `GuestId = ${member.GuestId}` });
      toast('Removed'); refresh();
    } catch { toast('Could not remove'); }
  };

  // Detach: the person stays a guest, just not part of this family any more.
  const detachMember = async (member) => {
    try {
      await UpdateGuest({ data: { FamilyId: 0, UpdatedBy: me }, where: `GuestId = ${member.GuestId}` });
      toast('Moved out of the family'); refresh();
    } catch { toast('Could not update'); }
  };

  return {
    canView, canEdit, canDelete, loading, families, totals, rows, functions, fnName,
    q, setQ, openFamily, setOpenFamily, addingTo, setAddingTo,
    addMember, removeMember, detachMember, refresh, fmtDate, event,
  };
}
